/**
 * Vacancy Data Adapter
 * Transforms backend vacancy data structure to frontend format
 *
 * IMPORTANT: Backend returns camelCase fields (TypeORM auto-converts from snake_case DB columns)
 *
 * NEW STRUCTURE (v2):
 * - Backend uses educations[] array with {levelId, studyIds[], years[]}
 * - This adapter converts educations to display format for UI
 * - Legacy studiegegevens still supported for backwards compatibility
 */

import { transformForDisplay } from '../educationTransform';

/**
 * Map backend work type to frontend format
 * Backend may return capitalized values, frontend expects lowercase
 *
 * @param {string} backendType - Backend typeDienstverband value
 * @returns {string} Frontend-compatible work type
 */
const mapWorkType = (backendType) => {
  if (!backendType) return 'studiebijbaan';

  const normalized = backendType.toLowerCase();

  const typeMap = {
    'stage': 'stage',
    'bijbaan': 'studiebijbaan',
    'studiebijbaan': 'studiebijbaan',
    'studie-gerelateerde bijbaan': 'studiebijbaan',
    'duaal traject': 'duaal',
    'duaal traject of bbl': 'duaal',
    'duaal-traject': 'duaal',
    'duaal': 'duaal',
  };

  return typeMap[normalized] || normalized;
};

/**
 * Format salary information from backend fields
 * Backend uses: brutoUurloonMin, brutoUurloonMax, salaryPerMonthMin, salaryPerMonthMax
 *
 * @param {Object} vacancy - Backend vacancy object
 * @returns {Object} Formatted salary data
 */
const formatSalary = (vacancy) => {
  const workType = mapWorkType(vacancy.typeDienstverband);

  // For internships (stage), prefer monthly compensation
  if (workType === 'stage' && vacancy.salaryPerMonthMin) {
    return {
      vergoeding: parseFloat(vacancy.salaryPerMonthMin) || null,
      vergoedingType: 'maand',
      vergoedingMin: parseFloat(vacancy.salaryPerMonthMin) || null,
      vergoedingMax: parseFloat(vacancy.salaryPerMonthMax) || null,
    };
  }

  // For jobs (bijbaan), use hourly wage
  if (vacancy.brutoUurloonMin) {
    return {
      vergoeding: parseFloat(vacancy.brutoUurloonMin) || null,
      vergoedingType: 'uurloon',
      vergoedingMin: parseFloat(vacancy.brutoUurloonMin) || null,
      vergoedingMax: parseFloat(vacancy.brutoUurloonMax) || null,
    };
  }

  // No salary specified
  return {
    vergoeding: null,
    vergoedingType: null,
    vergoedingMin: null,
    vergoedingMax: null,
  };
};

/**
 * Extract hours from JSONB object to frontend format
 * Backend may return: {"8-16 uur": true, "16-24 uur": true} or array ["8-16 uur", "16-24 uur"]
 *
 * @param {Object|Array} aantalUrenPerWeek - Backend hours data
 * @returns {Object} selectedHours object
 */
const formatHours = (aantalUrenPerWeek) => {
  if (!aantalUrenPerWeek) return {};

  // If already an object with true values, return as-is
  if (typeof aantalUrenPerWeek === 'object' && !Array.isArray(aantalUrenPerWeek)) {
    return aantalUrenPerWeek;
  }

  // If it's an array, convert to object
  if (Array.isArray(aantalUrenPerWeek)) {
    const selectedHours = {};
    aantalUrenPerWeek.forEach(uur => {
      selectedHours[uur] = true;
    });
    return selectedHours;
  }

  return {};
};

/**
 * Format location from postcode
 * Adds space in postcode if missing (e.g., "1012AB" → "1012 AB")
 *
 * @param {string} postcode - Dutch postcode
 * @returns {string} Formatted location string
 */
const formatLocation = (postcode) => {
  if (!postcode) return 'Locatie niet opgegeven';

  // Format postcode (add space if missing)
  const formatted = postcode.replace(/\s/g, '').toUpperCase();
  if (formatted.length === 6) {
    return `${formatted.slice(0, 4)} ${formatted.slice(4)}`;
  }

  return postcode;
};

/**
 * Transform backend vacancy object to frontend format
 *
 * Backend fields (camelCase from TypeORM):
 * - vacaturetitel, bedrijfsnaam, functieOmschrijving
 * - typeDienstverband, soortWerkvorm
 * - studiegegevens (JSONB - already in frontend format!)
 * - postcode, straalKm
 * - brutoUurloonMin/Max, salaryPerMonthMin/Max
 * - aantalUrenPerWeek (JSONB)
 * - status, publishedAt, createdAt
 *
 * Frontend format (camelCase):
 * - titel, bedrijfsnaam, beschrijving
 * - vacatureType, werkvorm
 * - selectedMBOStudies, selectedHBOStudies, etc. (from studiegegevens)
 * - locatie, postcode
 * - vergoeding, vergoedingType
 * - selectedHours (object)
 * - createdAt
 *
 * @param {Object} backendVacancy - Vacancy from backend API
 * @returns {Object} Vacancy in frontend format
 */
export const adaptVacancyFromBackend = (backendVacancy) => {
  if (!backendVacancy) {
    return null;
  }

  const salaryData = formatSalary(backendVacancy);
  const hoursData = formatHours(backendVacancy.aantalUrenPerWeek);

  // NEW: Transform educations[] to display format
  // Uses new ID-based structure: [{levelId, studyIds[], years[]}]
  const educationsDisplay = transformForDisplay(backendVacancy.educations);

  // LEGACY: Extract study data from studiegegevens JSONB
  // Backend already returns in frontend format, so just extract fields
  const studiegegevens = backendVacancy.studiegegevens || {};

  return {
    // Basic info
    id: backendVacancy.id,
    titel: backendVacancy.vacaturetitel || 'Vacature',
    bedrijfsnaam: backendVacancy.bedrijfsnaam || 'Bedrijf',
    beschrijving: backendVacancy.functieOmschrijving || '',
    bedrijfsomschrijving: backendVacancy.bedrijfsomschrijving || '',

    // Work type
    vacatureType: mapWorkType(backendVacancy.typeDienstverband),
    werkvorm: backendVacancy.soortWerkvorm || '',

    // Location
    locatie: backendVacancy.wijk && backendVacancy.stad
      ? `${backendVacancy.stad} - ${backendVacancy.wijk}`
      : backendVacancy.stad || formatLocation(backendVacancy.postcode),
    postcode: backendVacancy.postcode || '',
    straalKm: backendVacancy.straalKm || 25,
    stad: backendVacancy.stad || null,
    wijk: backendVacancy.wijk || null,
    distance: backendVacancy.distance, // From distance calculation (if student provided postcode)

    // Salary
    ...salaryData,

    // Hours
    uren: Array.isArray(backendVacancy.aantalUrenPerWeek)
      ? backendVacancy.aantalUrenPerWeek.join(', ')
      : Object.keys(hoursData).join(', ') || 'Niet opgegeven',
    selectedHours: hoursData,

    // NEW: Education display data from educations[] array
    // Contains: { levels: ["HBO Bachelor"], description: "HBO Bachelor - 2 studies - Jaar 3" }
    educations: backendVacancy.educations || [],
    educationLevels: educationsDisplay.levels,
    educationDescription: educationsDisplay.description,

    // LEGACY: Study requirements - Extract directly from studiegegevens JSONB
    // Backend already stores in frontend format (selectedHBOStudies, etc.)
    // Handle both naming conventions: selectedHBOLevels OR selectedHBOCategories
    selectedMBOStudies: studiegegevens.selectedMBOStudies || {},
    selectedHBOStudies: studiegegevens.selectedHBOStudies || {},
    selectedUniStudies: studiegegevens.selectedUniStudies || {},
    selectedMBOLevels: studiegegevens.selectedMBOLevels || {},
    selectedHBOLevels: studiegegevens.selectedHBOLevels || studiegegevens.selectedHBOCategories || {},
    selectedUniLevels: studiegegevens.selectedUniLevels || studiegegevens.selectedUniCategories || {},
    selectedMBOYears: studiegegevens.selectedMBOYears || {},
    selectedHBOYears: studiegegevens.selectedHBOYears || {},
    selectedUniYears: studiegegevens.selectedUniYears || {},
    studiegegevens: backendVacancy.studiegegevens, // Keep original for reference

    // Requirements and benefits
    vereisteKennis: backendVacancy.vereisteKennis || '',
    voordelen: backendVacancy.voordelen || '',
    doorgroeimogelijkheden: backendVacancy.doorgroeimogelijkheden || '',

    // Additional info
    vergoedingExtraInfo: backendVacancy.vergoedingExtraInfo || '',

    // Status and dates
    status: backendVacancy.status,
    createdAt: backendVacancy.publishedAt || backendVacancy.createdAt,
    publishedAt: backendVacancy.publishedAt,

    // Frontend-specific fields
    urgency: calculateUrgency({ publishedAt: backendVacancy.publishedAt }),
    tags: [], // Can be generated from studiegegevens if needed
    applicants: null, // Not available in current backend
  };
};

/**
 * Adapt multiple vacancies from backend
 *
 * @param {Array} backendVacancies - Array of backend vacancy objects
 * @returns {Array} Array of frontend-formatted vacancies
 */
export const adaptVacanciesFromBackend = (backendVacancies) => {
  if (!Array.isArray(backendVacancies)) {
    return [];
  }

  return backendVacancies
    .map(adaptVacancyFromBackend)
    .filter(Boolean); // Remove any null results
};

/**
 * Transform frontend search filters to backend format
 * (Used when sending search requests to the API)
 *
 * NEW API (v2):
 * - workType: "Stage" | "Studie-gerelateerde bijbaan" | "Duaal-traject"
 * - levelId: "HBO_BACHELOR" | "MBO_NIVEAU_4" etc.
 * - studyId: "HBO_BACHELOR_FINANCE_AND_CONTROL" etc.
 * - year: 1 | 2 | 3 | 4
 *
 * @param {Object} frontendFilters - Frontend filter object
 * @returns {Object} Backend-compatible filter object
 */
export const adaptFiltersToBackend = (frontendFilters) => {
  return {
    // New API format
    workType: frontendFilters.workType || frontendFilters.werktype,
    levelId: frontendFilters.levelId,
    studyId: frontendFilters.studyId,
    year: frontendFilters.year,

    // Location
    location: frontendFilters.location || undefined,
    radius: frontendFilters.radius || undefined,

    // Pagination
    page: frontendFilters.page || 1,
    limit: frontendFilters.limit || 20,
  };
};

/**
 * Calculate urgency based on vacancy publication date
 *
 * @param {Object} vacancy - Vacancy object with publishedAt field
 * @returns {string|null} Urgency level: 'high', 'medium', 'low', or null
 */
export const calculateUrgency = (vacancy) => {
  if (!vacancy || !vacancy.publishedAt) return null;

  const publishedDate = new Date(vacancy.publishedAt);
  const now = new Date();
  const daysSincePublished = Math.floor((now - publishedDate) / (1000 * 60 * 60 * 24));

  // Urgent if published in last 3 days
  if (daysSincePublished <= 3) return 'high';

  // Medium priority if published in last week
  if (daysSincePublished <= 7) return 'medium';

  // Low priority for older vacancies
  return 'low';
};

// Export all adapter functions
export default {
  adaptVacancyFromBackend,
  adaptVacanciesFromBackend,
  adaptFiltersToBackend,
  calculateUrgency,
};
